/**
 * Cómo se LLAMA cada persona en pantalla.
 *
 * Lissette, 2026-08-29: *"cómo se arregla el caso de que el primer agente dice
 * solo agent y cuando se agrega el otro agente dice agent owner?"*.
 *
 * EL PROBLEMA ES DE PALABRAS, NO DE PERMISOS. Un plan Solo con dos agentes son
 * DOS AGENTES IGUALES compartiendo un CRM — los dos son dueños, comparten
 * clientes, comisiones, metas y asientos, y solo se reparten a quién atiende
 * cada quien. Pero la pantalla decía lo contrario:
 *
 *     Josef  →  🛡️ Agente Solo        ("solo" = está solo, y ya no lo está)
 *     Nina   →  👑 Admin de agencia    (corona = manda sobre el otro)
 *
 * Leído así parece que Nina está por encima de Josef, cuando es al revés de
 * como funciona: ninguno está por encima. Y "Agente Solo" pasa a ser falso en
 * el momento en que se agrega al segundo.
 *
 * LA REGLA: cuando en la misma cuenta conviven un `solo_agent` y un
 * `agency_owner`, los dos se pintan IGUAL —mismo icono, misma palabra— y al
 * agregado se le añade la coletilla "Admin", que es lo que de verdad lo
 * distingue: entró por el complemento de admin. Sin corona y sin "solo".
 *
 * En cualquier otra cuenta —una agencia de verdad, o un agente que trabaja
 * realmente solo— no cambia ni una letra.
 *
 * Un SOLO SITIO para esto a propósito: había CINCO copias de la tabla de
 * nombres repartidas por la app y ya se habían separado entre ellas (unas en
 * español, otras en inglés fijo). Es el E-138 esperando a pasar otra vez.
 */

/** Los roles que producen pólizas. Los asientos no entran. */
const AGENTES = new Set(['agency_owner', 'agency_admin', 'agent', 'solo_agent']);

/**
 * ¿Es esta una cuenta de DOS AGENTES IGUALES (plan Solo + complemento admin)?
 *
 * Se reconoce por la forma que deja ese complemento: invita a la persona como
 * agente y al aceptar la asciende a `agency_owner`, así que la cuenta acaba
 * con un `solo_agent` (quien la abrió) y un `agency_owner` (el agregado).
 * Una agencia de verdad no tiene ningún `solo_agent`.
 *
 * @param {Array} users  el equipo de la cuenta
 */
export function esCuentaDeDosAgentes(users) {
  const roles = (users || []).filter((u) => u && AGENTES.has(u.role)).map((u) => u.role);
  return roles.includes('solo_agent') && roles.includes('agency_owner');
}

const BASE = {
  agency_owner: { icono: '👑', es: 'Admin de agencia', en: 'Agency Admin' },
  agency_admin: { icono: '🔧', es: 'Admin', en: 'Admin' },
  agent: { icono: '👤', es: 'Agente', en: 'Agent' },
  solo_agent: { icono: '🛡️', es: 'Agente Solo', en: 'Solo Agent' },
  assistant: { icono: '💺', es: 'Asiento', en: 'Seat' },
};

/**
 * El nombre del rol tal cual se enseña.
 *
 * @param {object} user           la persona
 * @param {Array}  users          el equipo entero (hace falta para saber la forma de la cuenta)
 * @param {'es'|'en'} lang
 * @returns {{ icono: string, texto: string, etiqueta: string }}
 *   `etiqueta` = icono + texto, que es lo que casi todas las pantallas pintan.
 */
export function nombreDelRol(user, users, lang = 'es') {
  const es = lang === 'es';
  const rol = (user && user.role) || '';

  if (esCuentaDeDosAgentes(users) && (rol === 'solo_agent' || rol === 'agency_owner')) {
    // Mismo icono para los dos: la corona era la mitad del malentendido.
    const texto = rol === 'agency_owner'
      ? (es ? 'Agente (Admin)' : 'Agent (Admin)')
      : (es ? 'Agente' : 'Agent');
    return { icono: '👤', texto, etiqueta: `👤 ${texto}` };
  }

  const b = BASE[rol];
  if (!b) return { icono: '', texto: rol, etiqueta: rol };
  const texto = es ? b.es : b.en;
  return { icono: b.icono, texto, etiqueta: `${b.icono} ${texto}` };
}
